/**
 * Image format detection and classification: file signatures, accepted mime type lists and browser
 * preview limitations. The module is intentionally dependency free so tests can import it as well.
 */

export const MIME_BMP = 'image/bmp';
export const MIME_PNG = 'image/png';
export const MIME_GIF = 'image/gif';
export const MIME_JPG = 'image/jpeg';
export const MIME_ICO = 'image/x-icon';
export const MIME_WEBP = 'image/webp';
export const MIME_SVG = 'image/svg+xml';
export const MIME_SVG_SHORT = 'image/svg';
export const MIME_BMP_MS = 'image/x-ms-bmp';
export const MIME_AVIF = 'image/avif';
export const MIME_HEIC = 'image/heic';
export const MIME_HEIF = 'image/heif';
export const MIME_PDF = 'application/pdf';

const MIME_SIGNATURES_MAP_2 = {
  '424d': MIME_BMP,
};

const MIME_SIGNATURES_MAP_4 = {
  ffd8ffe0: MIME_JPG,
  ffd8ffe1: MIME_JPG,
  ffd8ffe2: MIME_JPG,
  ffd8ffe3: MIME_JPG,
  ffd8ffe8: MIME_JPG,
  '00000100': MIME_ICO,
  '00000200': MIME_ICO,
  25504446: MIME_PDF,
};

const MIME_SIGNATURES_MAP_6 = {
  474946383761: MIME_GIF,
  474946383961: MIME_GIF,
  '3c3f786d6c20': MIME_SVG,
};

const MIME_SIGNATURES_MAP_8 = {
  '89504e470d0a1a0a': MIME_PNG,
};

const MIME_SIGNATURES_MAP_14 = {
  '5249464600000000574542505650': MIME_WEBP,
};

export async function getMIME(blob) {
  const buf = await blob.slice(0, 16).arrayBuffer();
  const arr = new Uint8Array(buf);
  let signature = '';
  let mime;
  const isoBmffMime = getIsoBmffMime(arr);
  if (isoBmffMime) {
    return isoBmffMime;
  }
  for (let i = 0; i < arr.length; i++) {
    if (i === 2 && signature in MIME_SIGNATURES_MAP_2) {
      mime = MIME_SIGNATURES_MAP_2[signature];
      break;
    } else if (i === 4 && signature in MIME_SIGNATURES_MAP_4) {
      mime = MIME_SIGNATURES_MAP_4[signature];
      break;
    } else if (i === 6 && signature in MIME_SIGNATURES_MAP_6) {
      mime = MIME_SIGNATURES_MAP_6[signature];
      break;
    } else if (i === 8 && signature in MIME_SIGNATURES_MAP_8) {
      mime = MIME_SIGNATURES_MAP_8[signature];
      break;
    } else if (i === 14) {
      if (isWebp(signature)) {
        mime = MIME_WEBP;
      }
      break;
    }
    signature += arr[i].toString(16).padStart(2, '0');
  }
  return mime || blob.type || 'unknown';
}

// AVIF/HEIC/HEIF use the ISOBMFF container: box size (4 bytes), 'ftyp', major brand
const ISO_BMFF_BRANDS = {
  avif: MIME_AVIF,
  avis: MIME_AVIF,
  heic: MIME_HEIC,
  heix: MIME_HEIC,
  hevc: MIME_HEIC,
  hevx: MIME_HEIC,
  heim: MIME_HEIC,
  heis: MIME_HEIC,
  hevm: MIME_HEIC,
  hevs: MIME_HEIC,
  mif1: MIME_HEIF,
  msf1: MIME_HEIF,
};

function getIsoBmffMime(arr) {
  const toAscii = (from, to) => String.fromCharCode(...arr.slice(from, to));
  if (arr.length < 12 || toAscii(4, 8) !== 'ftyp') {
    return null;
  }
  return ISO_BMFF_BRANDS[toAscii(8, 12)] || null;
}

function isWebp(signature) {
  const arr = [...signature];
  arr.splice(8, 8, ...Array(8).fill('0'));
  const masked = arr.join('');
  return MIME_SIGNATURES_MAP_14[masked] === MIME_WEBP;
}

export const ACCEPTED_MIME_TYPES = new Set([
  MIME_BMP,
  MIME_BMP_MS,
  MIME_PNG,
  MIME_GIF,
  MIME_JPG,
  MIME_ICO,
  MIME_WEBP,
  MIME_SVG,
  MIME_SVG_SHORT,
  MIME_AVIF,
  MIME_HEIC,
  MIME_HEIF,
]);

export const ACCEPTED_IMAGE_ACCEPT_ATTR = [
  ...ACCEPTED_MIME_TYPES,
  '.avif',
  '.bmp',
  '.gif',
  '.heic',
  '.heif',
  '.ico',
  '.jpeg',
  '.jpg',
  '.png',
  '.svg',
  '.webp',
].join(',');

// The image backend rejects ICO even though the accept attribute offers it — this list mirrors the API validator
export const UPLOAD_ACCEPTED_IMAGE_MIMES = new Set([
  MIME_BMP,
  MIME_BMP_MS,
  MIME_PNG,
  MIME_GIF,
  MIME_JPG,
  MIME_WEBP,
  MIME_SVG,
  MIME_SVG_SHORT,
  MIME_AVIF,
  MIME_HEIC,
  MIME_HEIF,
]);

export const UPLOAD_ACCEPTED_IMAGE_FORMATS_LABEL = 'BMP, GIF, JPEG, PNG, SVG, WEBP, AVIF, HEIC or HEIF';

// For places that crop and process the image in the browser: HEIC/HEIF cannot be decoded
// and SVG is not raster, so neither the cropper nor the recognizer can work with them
export const PROCESSABLE_IMAGE_FORMATS_LABEL = 'BMP, GIF, JPEG, PNG, WEBP or AVIF';

const UNPROCESSABLE_IMAGE_MIMES = new Set([MIME_HEIC, MIME_HEIF, MIME_SVG, MIME_SVG_SHORT]);

const UNPROCESSABLE_IMAGE_EXTENSIONS = ['heic', 'heif', 'svg'];

/**
 * @param {string|undefined} mimeOrSource mime type, file name or url
 * @return {boolean}
 */
export function isUnprocessableImage(mimeOrSource) {
  const source = (mimeOrSource || '').toString().toLowerCase();
  if (!source) {
    return false;
  }
  if (UNPROCESSABLE_IMAGE_MIMES.has(source)) {
    return true;
  }
  const [path] = source.split(/[?#]/);
  return UNPROCESSABLE_IMAGE_EXTENSIONS.some((ext) => path.endsWith(`.${ext}`));
}

// Most browsers cannot decode HEIC/HEIF, so such files get a placeholder instead of a preview
export const UNPREVIEWABLE_IMAGE_MIMES = new Set([MIME_HEIC, MIME_HEIF]);

const UNPREVIEWABLE_IMAGE_EXTENSIONS = ['heic', 'heif'];

/**
 * @param {string|undefined} mimeOrSource mime type, file name or url
 * @return {boolean}
 */
export function isUnpreviewableImage(mimeOrSource) {
  const source = (mimeOrSource || '').toString().toLowerCase();
  if (!source) {
    return false;
  }
  if (UNPREVIEWABLE_IMAGE_MIMES.has(source)) {
    return true;
  }
  const [path] = source.split(/[?#]/);
  return UNPREVIEWABLE_IMAGE_EXTENSIONS.some((ext) => path.endsWith(`.${ext}`));
}

/**
 * @param {string|undefined} mimeOrSource mime type, file name or url
 * @return {string} short format label for the UI: HEIC, PNG, ...
 */
export function getImageFormatLabel(mimeOrSource) {
  const [source] = (mimeOrSource || '').toString().toLowerCase().split(/[?#]/);
  if (source.startsWith('image/')) {
    return source.replace('image/', '').replace('+xml', '').toUpperCase();
  }
  // a domain name also contains dots, so the extension is taken from the last path segment only
  const extension = source.split('/').pop().split('.').slice(1).pop() || '';
  return /^[a-z0-9]{2,5}$/.test(extension) ? extension.toUpperCase() : '';
}

/**
 * @param {string|undefined} source file name or url
 * @return {string} file name without path and query, '' when a mime type was passed instead
 */
export function getImageFileName(source) {
  const [path] = (source || '').toString().split(/[?#]/);
  if (!path || path.toLowerCase().startsWith('image/')) {
    return '';
  }
  return decodeURIComponent(path.split('/').pop() || '');
}

/**
 * Builds the placeholder caption for files the browser cannot preview: the file name is shortened,
 * the extension is always kept so the user still sees the format.
 * @param {string|undefined} source file name or url
 * @param {string|undefined} mime used when the name carries no extension
 * @param {number} maxBaseLength
 * @return {string} for example "verylongimagen….heic"
 */
export function getImageFileLabel(source, mime = '', maxBaseLength = 14) {
  const name = getImageFileName(source);
  if (!name) {
    return '';
  }
  const parts = name.split('.');
  const extension = parts.length > 1 ? parts.pop() : getImageFormatLabel(mime).toLowerCase();
  const base = parts.join('.') || name;
  const shortBase = base.length > maxBaseLength ? `${base.slice(0, maxBaseLength)}…` : base;
  return extension ? `${shortBase}.${extension}` : shortBase;
}
